import React from "react";
import { observer } from "mobx-react-lite";
import { useGlobalFilters } from "../../hooks/useGlobalFilters";
import { combinedChartStore } from "./CombinedChartStore";
import { predictionTrendsStore } from "../../Analysis_A/PredictionTrendsStore";
import DataWindowSlider from "../../components/DataWindowSlider"; // ✅ подключаем слайдер
import ChartPointDataModal from "../../components/ChartPointDataModal";
import { format } from "date-fns";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import "./BySeasons.scss";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

function formatToYearQuarter(dateString) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  let quarter;
  if (month <= 3) quarter = "Q1";
  else if (month <= 6) quarter = "Q2";
  else if (month <= 9) quarter = "Q3";
  else quarter = "Q4";
  return `${year}-${quarter}`;
}

function getGroupNamesByIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.map((id) => {
    const group = predictionTrendsStore.groups.find((g) => String(g.id) === String(id));
    return group ? group.name : id;
  });
}

function formatNumber(val) {
  if (typeof val === "number") {
    return val.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  if (!isNaN(Number(val))) {
    return Number(val).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  return val ?? "";
}

function getQuarterDateRange(label) {
  if (!label) return null;
  const match = label.match(/^(\d{4})-Q([1-4])$/);
  if (!match) return null;
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  const startMonth = (quarter - 1) * 3;
  const startDate = new Date(Date.UTC(year, startMonth, 1));
  const endDate = new Date(Date.UTC(year, startMonth + 3, 0));
  return {
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
  };
}

const CombinedChart = observer(() => {
  const globalFilters = useGlobalFilters();
  const store = combinedChartStore;

  // ✅ локальное состояние для окна
  const [dataWindow, setDataWindow] = React.useState({ start: 0, end: 100 });
  const [selectedInterval, setSelectedInterval] = React.useState(null);
  const [modalRows, setModalRows] = React.useState([]);
  const [isModalLoading, setIsModalLoading] = React.useState(false);

  React.useEffect(() => {
    store.fetchData(globalFilters.filters);
    // eslint-disable-next-line
  }, [globalFilters.filtersString]);

  // Сброс окна при обновлении данных
  React.useEffect(() => {
    setDataWindow({ start: 0, end: 100 });
  }, [store.data.length]);

  // Обновление окна
  const updateWindow = (value) => {
    const newStart = Number(value);
    setDataWindow({ start: newStart, end: newStart + 100 });
  };

  // Делаем срез данных
  const allData = store.data || [];
  const visibleData = React.useMemo(() => {
    if (allData.length === 0) return [];
    const startIndex = Math.min(dataWindow.start, allData.length - 1);
    const endIndex = Math.min(dataWindow.end, allData.length);
    return allData.slice(startIndex, endIndex);
  }, [allData, dataWindow]);

  // Подпись под слайдером
  const dateRangeText = React.useMemo(() => {
    if (visibleData.length === 0) return "Нет данных";
    const first = visibleData[0];
    const last = visibleData[visibleData.length - 1];
    return `Показаны данные: ${formatToYearQuarter(first.quarter)} - ${formatToYearQuarter(
      last.quarter
    )}`;
  }, [visibleData]);

  // Проверка выбранных групп
  const hasSelectedGroups =
    globalFilters.selectedGroups && globalFilters.selectedGroups.length > 0;

  const handlePointClick = React.useCallback(
    async ({ label }) => {
      if (!label || !globalFilters.selectedGroups?.length) return;
      const range = getQuarterDateRange(label);
      if (!range) return;

      setSelectedInterval(label);
      setModalRows([]);
      setIsModalLoading(true);

      try {
        const rows = await store.fetchPostsForModal({
          groupIds: globalFilters.selectedGroups,
          startDate: range.startDate,
          endDate: range.endDate,
        });
        setModalRows(rows);
      } catch (error) {
        console.error("Ошибка при загрузке данных для модального окна:", error);
      } finally {
        setIsModalLoading(false);
      }
    },
    [globalFilters.selectedGroups, store]
  );

  // Ранние возвраты после всех хуков
  if (!hasSelectedGroups) return <div>Нет выбранной группы</div>;
  if (store.loading) return <div>Загрузка...</div>;
  if (store.error) return <div>Ошибка: {store.error.message}</div>;
  if (!allData || allData.length === 0) return <div>Нет данных для отображения</div>;

  // --- строим график на visibleData ---
  const labels = visibleData.map((d) => formatToYearQuarter(d.quarter));
  const barData = visibleData.map((d) => d.bar_value);
  const redLine = visibleData.map((d) => d.red_value);
  const greenLine = visibleData.map((d) => d.green_value);

  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Индекс сезонной эмоциональной напряженности",
        data: barData,
        backgroundColor: "rgba(52, 152, 219, 0.7)",
        borderColor: "rgba(52, 152, 219, 1)",
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 20,
      },
      {
        type: "line",
        label: "Уровень агрессии",
        data: redLine,
        borderColor: "rgba(231, 76, 60, 1)",
        backgroundColor: "rgba(231, 76, 60, 0.2)",
        tension: 0.3,
        fill: false,
        pointRadius: 5,
        pointBackgroundColor: "rgba(231, 76, 60, 1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Уровень апатии",
        data: greenLine,
        borderColor: "rgba(46, 204, 113, 1)",
        backgroundColor: "rgba(46, 204, 113, 0.2)",
        tension: 0.3,
        fill: false,
        pointRadius: 5,
        pointBackgroundColor: "rgba(46, 204, 113, 1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        yAxisID: "y",
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 2.5,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 14, family: "'Roboto', sans-serif", weight: "500" },
          color: "#34495e",
          padding: 15,
          boxWidth: 20,
          usePointStyle: true,
        },
      },
      title: {
        display: true,
        text: "График по кварталам",
        font: { size: 20, family: "'Roboto', sans-serif", weight: "600" },
        color: "#2c3e50",
        padding: { top: 10, bottom: 20 },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(44, 62, 80, 0.9)",
        titleFont: { size: 14, family: "'Roboto', sans-serif", weight: "500" },
        bodyFont: { size: 12, family: "'Roboto', sans-serif" },
        padding: 10,
        cornerRadius: 6,
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Квартал",
          font: { size: 14, family: "'Roboto', sans-serif", weight: "500" },
          color: "#34495e",
        },
        ticks: {
          font: { size: 12, family: "'Roboto', sans-serif" },
          color: "#7f8c8d",
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          display: false,
        },
      },
      y: {
        title: {
          display: true,
          text: "Значение",
          font: { size: 14, family: "'Roboto', sans-serif", weight: "500" },
          color: "#34495e",
        },
        ticks: {
          font: { size: 12, family: "'Roboto', sans-serif" },
          color: "#7f8c8d",
          stepSize: 0.2,
        },
        grid: {
          color: "rgba(149, 165, 166, 0.2)",
          borderDash: [5, 5],
        },
        beginAtZero: true,
      },
    },
    animation: {
      duration: 1200,
      easing: "easeOutCubic",
    },
    hover: {
      mode: "index",
      intersect: false,
    },

    onClick: (event, elements, chart) => {
      if (!elements.length) return;

      const el = elements[0];
      const datasetIndex = el.datasetIndex;
      const dataIndex = el.index;

      const dataset = chart.data.datasets[datasetIndex];
      const value = dataset.data[dataIndex];
      const label = chart.data.labels[dataIndex];

      handlePointClick({ datasetIndex, dataIndex, label, value });
    },
  };

  // Экспорт в Excel
  const handleExportExcel = async () => {
    const allData = await store.fetchAllData(globalFilters.filtersString);
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups); // на русском

    const graphTitle = "Эмоциональная_напряженность_по_сезонам";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.xlsx`;

    const ws_data = [
      ["Date", "bar_value", "red_value", "green_value"],
      ...allData.map(d => [
        formatToYearQuarter(d.quarter),
        formatNumber(d.bar_value),
        formatNumber(d.red_value),
        formatNumber(d.green_value)
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Данные");
    XLSX.writeFile(wb, fileName);
  };

  // Экспорт в PDF
  const handleExportPDF = async () => {
    const allData = await store.fetchAllData(globalFilters.filtersString);
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups); // на русском

    const graphTitle = "Эмоциональная_напряженность_по_сезонам";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.pdf`;

    const input = document.querySelector("#chart-container");
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(imgData, "PNG", 10, 10, 270, 80);

    autoTable(pdf, {
      startY: 100,
      head: [["Date", "bar_value", "red_value", "green_value"]],
      body: allData.map(d => [
        formatToYearQuarter(d.quarter),
        formatNumber(d.bar_value),
        formatNumber(d.red_value),
        formatNumber(d.green_value)
      ]),
      styles: { font: "helvetica", fontSize: 10 }
    });
    pdf.save(fileName);
  };

  // Кнопки справа снизу под графиком
  return (
    <div className="by-seasons">
      <div className="by-seasons-container">
      <div style={{ marginBottom: 16 }}>
        <div className="info-block" style={{
          background: '#e3f2fd',
          borderRadius: '12px',
          padding: '20px 28px',
          marginBottom: '12px',
          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)'
        }}>
          <h3 style={{ color: '#1976d2', marginTop: 0, marginBottom: 12, fontWeight: 600 }}>
            Справка
          </h3>

          <div>
<p>Для каждого дня сначала рассчитывается средний коэффициент Трейгера по всем записям выбранных сообществ.
   Индекс за квартал равен отношению числа дней с отклонением от среднего больше одного стандартного отклонения
   к числу остальных дней. Линии агрессии и апатии показывают доли отклонений вверх и вниз.</p>

<p>Преобладание того или иного уровня в составе индекса указывает на причину роста эмоциональной 
  напряженности: эмоциональная напряженность может иметь агрессивный характер, либо 
  тревожно-апатичный характер.</p>
</div>

         
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 24,
        marginRight: 24
      }}>
        <button className="by-seasons-export-btn" onClick={handleExportExcel}>Экспорт в Excel</button>
        <button className="by-seasons-export-btn" onClick={handleExportPDF}>Экспорт в PDF</button>
      </div>



      <div
        id="chart-container"
        style={{
          height: 500,
          maxWidth: 1400,
          margin: '0 auto',
          background: '#f8fafc',
          borderRadius: 20,
          boxShadow: '0 4px 24px rgba(25, 118, 210, 0.08)',
          position: 'relative'
        }}
      >
        {allData.length > 100 && (
          <div style={{ padding: '12px 16px 0', display: 'flex', justifyContent: 'center' }}>
            <DataWindowSlider
              aggregation={'day'}
              dataWindow={dataWindow}
              maxDataLength={allData.length}
              updateWindow={updateWindow}
              dateRangeText={dateRangeText}
            />
          </div>
        )}
        <div style={{ height: 'calc(100% - 60px)', width: '100%' }}>
          <Bar data={chartData} options={options} />
        </div>
      </div>

      <ChartPointDataModal
        date={
          isModalLoading && !modalRows.length
            ? `${selectedInterval || ""} (загрузка...)`
            : selectedInterval
        }
        rows={modalRows}
        onClose={() => {
          setSelectedInterval(null);
          setModalRows([]);
          setIsModalLoading(false);
        }}
      />
      </div>
    </div>
  );
});

export default CombinedChart;
